import ClientInvoiceFormOptionsArray from '../Enums/ClientInvoiceFormOptionsArray';

// The choices and checks of the automatic invoicing setup's invoice in the edit wizard
// (InvoiceAutomationWindowDescription). Options are { value, label }; the first, empty one is the "Choose one…" a
// select shows before anything is chosen.

const ChooseOneOption = { value: '', label: 'Choose one…', disabled: true };

// The invoice forms of the invoice form's own list.
export function getInvoiceFormOptions() {
  const invoiceForms = ClientInvoiceFormOptionsArray.filter((option) => !!option.value);
  return [ChooseOneOption, ...invoiceForms];
}

// A layout choice (form order or group) that may be left empty: its list with "None" for the empty choice.
export function getOptionalChoiceOptions(options) {
  return options.map((option) => ({ value: option.value ?? '', label: option.label || 'None' }));
}

// The text with {Month} or {Year} put in where the cursor is, a space kept on each side of it.
export function insertPlaceholder(text: string, placeholder: string, position: number) {
  const before = text.slice(0, position);
  const after = text.slice(position);
  const spaceBefore = before !== '' && !before.endsWith(' ') ? ' ' : '';
  const spaceAfter = after !== '' && !after.startsWith(' ') ? ' ' : '';
  return `${before}${spaceBefore}${placeholder}${spaceAfter}${after}`;
}

// The bill-to contact chosen on the edited setup, from the client's contacts it carries (ClientContacts), as the
// invoice form does; undefined before one is chosen.
export function getChosenContact(setup) {
  return (setup.ClientContacts ?? []).find((contact) => String(contact.contactId) === String(setup.ContactId));
}

// Days to pay must be given, as a whole number of 0 or more. The invoice form's check lets an empty field through,
// which would be saved as 0.
export function checkDaysToPay(value) {
  if (value === '' || value === null || value === undefined) {
    return "This field can't be empty. ";
  }
  const daysToPay = Number(value);
  if (!Number.isInteger(daysToPay) || daysToPay < 0) {
    return 'This value has to be a whole number of 0 or more. ';
  }
  return '';
}

// The late fee must be given, as 0 or more.
export function checkLateFee(value) {
  if (value === '' || value === null || value === undefined) {
    return "This field can't be empty. ";
  }
  if (Number.isNaN(Number(value)) || Number(value) < 0) {
    return 'This value has to be at least zero. ';
  }
  return '';
}

// Whether a field still holds the value a new setup took from the project's latest invoice (the AI suggestion).
export function isSuggestedValue(setup, fieldName) {
  const suggestedValues = setup.SuggestedValues ?? {};
  return fieldName in suggestedValues && isSameValue(setup[fieldName], suggestedValues[fieldName]);
}

// Whether a field still holds a value: as text, as the wizard's inputs give text back, or as the same number.
function isSameValue(value, otherValue) {
  const text = String(value ?? '');
  const otherText = String(otherValue ?? '');
  if (text === otherText) {
    return true;
  }
  return text !== '' && otherText !== '' && Number(text) === Number(otherText);
}

// The client's projects that can go on the setup's invoice, as the wizard's choice offers them: "#id name".
export function getOtherProjectOptions(setup) {
  return (setup.ClientProjects ?? []).map((clientProject) => ({
    value: clientProject.projectId,
    label: formatProjectLabel(clientProject.projectId, clientProject.projectName),
  }));
}

// A project as the setup page names it: "#id name", or "#id" without a name.
export function formatProjectLabel(projectId, projectName) {
  return projectName ? `#${projectId} ${projectName}` : `#${projectId}`;
}

// The edited setup as the save takes it: the choices and the numbers as numbers, an empty layout choice as nothing,
// and without what is there only for the page: the client's contacts, to show the chosen one's details, the suggested
// values, the client's projects to choose from and the project whose setup invoices this one.
export function getSetupToSave(setup) {
  const {
    ClientContacts,
    SuggestedValues,
    ClientProjects,
    InvoicedWithProjectId,
    InvoicedWithProjectName,
    ...setupToSave
  } = setup;
  return {
    ...setupToSave,
    ContactId: Number(setupToSave.ContactId),
    CurrencyId: Number(setupToSave.CurrencyId),
    DaysToPay: Number(setupToSave.DaysToPay),
    LatePaymentInterestRate: Number(setupToSave.LatePaymentInterestRate),
    ReportOrder: setupToSave.ReportOrder || null,
    ReportGroup1: setupToSave.ReportGroup1 || null,
    ReportGroup2: setupToSave.ReportGroup2 || null,
  };
}
